import React, { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useNavigate } from 'react-router'
import { SupplierRecovery, User } from '../../../models'
import { PaginatedTableContract } from '../../common'
import { currencyFormat } from '../../../helpers/currencyCalculation'
import defineAbilityFor from './../defineAbility'
import { getAuthUser } from '../../../helpers'
import { getUser, updateSupplierRecoveryAssignedUser, toastSuccess } from '../../../api'

interface SupplierRecoveryTableProps extends PaginatedTableContract {
    setSelectedSuppliers: (selectedSuppliers: number[]) => void
    resources: {
        supplierRecovery: SupplierRecovery[]
    }
    users: User[]
    selectedSuppliers: number[]
    toggleSupplierSelection: (id: number) => void
}

export const SupplierRecoveryTable = (props: SupplierRecoveryTableProps) => {
    const ability = defineAbilityFor(getAuthUser())
    const { resources, selectedSuppliers, toggleSupplierSelection, users, callbacks } = props
    const { supplierRecovery } = resources

    const canAssign = ability.can('Assign', 'Supplier')

    const [assignedUserNames, setAssignedUserNames] = useState<{
        [key: number]: string | null
    }>({})
    const [showAssignForm, setShowAssignForm] = useState<boolean>(false)
    const [currentSupplierId, setCurrentSupplierId] = useState<number | null>(null)
    const [assignedUserId, setAssignedUserId] = useState<string>('')
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const navigate = useNavigate()

    useEffect(() => {
        let isCancelled = false

        const fetchAssignedUserNames = async () => {
            const userIds = supplierRecovery
                .filter((supplier) => supplier.assignedUser)
                .map((supplier) => supplier.assignedUser)
            const uniqueUserIds = Array.from(new Set(userIds))

            try {
                const userPromises = uniqueUserIds.map((id) => getUser(id.toString()))
                const userResponses = await Promise.all(userPromises)

                if (!isCancelled) {
                    const userData = userResponses.reduce((acc, result) => {
                        if (result.data) {
                            acc[result.data.id] = result.data.name
                        }
                        return acc
                    }, {} as { [key: number]: string })

                    const updatedAssignedUserNames = supplierRecovery.reduce(
                        (acc, supplier) => {
                            if (supplier.assignedUser) {
                                acc[supplier.supplierId] =
                                    userData[supplier.assignedUser] || null
                            }
                            return acc
                        },
                        {} as { [key: number]: string | null }
                    )

                    setAssignedUserNames(updatedAssignedUserNames)
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error('Failed to fetch assigned user names:', error)
                }
            }
        }

        fetchAssignedUserNames()

        return () => {
            isCancelled = true
        }
    }, [supplierRecovery])

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (assignedUserId && currentSupplierId !== null) {
            const params = {
                assigned_user_id: assignedUserId,
                selected_suppliers: [currentSupplierId],
                reassign: true,
            }
            setIsSubmitting(true)

            updateSupplierRecoveryAssignedUser(params)
                .then(() => {
                    setIsSubmitting(false)
                    setShowAssignForm(false)
                    setCurrentSupplierId(null)
                    setAssignedUserId('')
                    callbacks.setReload((prev) => !prev)
                    toastSuccess('Suppliers Assigned')
                })
                .catch(() => {
                    setIsSubmitting(false)
                })
        }
    }

    const handleReassignClick = (supplierId: number) => {
        setShowAssignForm(true)
        setCurrentSupplierId(supplierId)
    }

    const handleUnassignClick = (supplierId: number) => {
        const params = {
            assigned_user_id: '',
            selected_suppliers: [supplierId],
        }

        updateSupplierRecoveryAssignedUser(params)
            .then(() => {
                navigate(0)
                toastSuccess('Supplier unassigned')
            })
            .catch(() => {
                setIsSubmitting(false)
            })
    }

    const assignableSupplierIds = supplierRecovery
        .filter((supplier) => supplier.assignedUser == null)
        .map((supplier) => supplier.supplierId)

    const isAllSelected =
        assignableSupplierIds.length > 0 &&
        assignableSupplierIds.every((id) => selectedSuppliers.includes(id))

    const handleSelectAll = () => {
        if (isAllSelected) {
            props.setSelectedSuppliers(
                selectedSuppliers.filter((id) => !assignableSupplierIds.includes(id))
            )
        } else {
            props.setSelectedSuppliers(
                Array.from(new Set([...selectedSuppliers, ...assignableSupplierIds]))
            )
        }
    }

    const assignedSuppliersCount = props.users.find(
        (user) => user.id === Number(assignedUserId)
    )?.assignedSupplierCount
    const maximumLimit = assignedSuppliersCount ? assignedSuppliersCount >= 200 : undefined

    return (
        <table className="table w-100" style={{ tableLayout: 'auto' }}>
            <tbody>
                <tr>
                    {canAssign ? (
                        <th>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={handleSelectAll}
                                disabled={assignableSupplierIds.length === 0}
                                title="Select all unassigned"
                            />
                        </th>
                    ) : null}
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Balance</th>
                    <th>Last Updated</th>
                    <th>Flag</th>
                    {canAssign && (
                        <>
                            <th>Assigned User</th>
                            {!showAssignForm && <th>Action</th>}
                        </>
                    )}
                </tr>
                {supplierRecovery.map((supplierRecovery) => (
                    <tr key={supplierRecovery.id}>
                        {canAssign ? (
                            <td>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    disabled={supplierRecovery.assignedUser != null}
                                    checked={
                                        selectedSuppliers.includes(
                                            supplierRecovery.supplierId
                                        ) || supplierRecovery.assignedUser != null
                                    }
                                    onChange={() =>
                                        toggleSupplierSelection(supplierRecovery.supplierId)
                                    }
                                />
                            </td>
                        ) : null}
                        <td className="text-break">
                            <Link to={`/suppliers/${supplierRecovery.supplierId}`}>
                                {supplierRecovery.supplierName}
                            </Link>
                        </td>
                        <td>{supplierRecovery.supplierPhone}</td>
                        <td>{currencyFormat(supplierRecovery.balance)}</td>
                        <td>{supplierRecovery.updated_at.format('MMM DD, YYYY')}</td>
                        <td>
                            <span
                                className={`d-inline-block rounded-circle ${
                                    supplierRecovery.hasRecentlyPaid
                                        ? 'bg-success'
                                        : 'bg-danger'
                                }`}
                                style={{ width: '15px', height: '15px' }}
                            ></span>
                        </td>
                        {canAssign && (
                            <td>
                                {showAssignForm &&
                                currentSupplierId === supplierRecovery.supplierId ? (
                                    <form
                                        onSubmit={handleSubmit}
                                        className="d-flex align-items-center"
                                    >
                                        <div style={{ marginRight: '10px' }}>
                                            <select
                                                className="form-select mb-2"
                                                id="usersInput"
                                                value={assignedUserId}
                                                onChange={(e) =>
                                                    setAssignedUserId(e.target.value)
                                                }
                                                required
                                            >
                                                <option value="">Select User</option>
                                                {users.map((user) => (
                                                    <option
                                                        key={`user_option_${user.id}`}
                                                        value={user.id}
                                                    >
                                                        {user.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            style={{ marginRight: '10px' }}
                                            disabled={isSubmitting}
                                        >
                                            Assign
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setShowAssignForm(false)
                                                setCurrentSupplierId(null)
                                                setAssignedUserId('')
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </form>
                                ) : (
                                    <span>
                                        {assignedUserNames[supplierRecovery.supplierId] ||
                                            'Unassigned'}
                                    </span>
                                )}
                            </td>
                        )}
                        {canAssign && (
                            <td className="align-middle text-nowrap">
                                {!showAssignForm && (
                                    <div className="d-flex flex-nowrap gap-1 align-items-center">
                                        <button
                                            type="button"
                                            className="btn btn-warning btn-sm"
                                            onClick={() =>
                                                handleReassignClick(
                                                    supplierRecovery.supplierId
                                                )
                                            }
                                            disabled={
                                                supplierRecovery.assignedUser == null ||
                                                maximumLimit
                                            }
                                        >
                                            Reassign
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            disabled={supplierRecovery.assignedUser == null}
                                            onClick={() =>
                                                handleUnassignClick(
                                                    supplierRecovery.supplierId
                                                )
                                            }
                                        >
                                            Unassign
                                        </button>
                                    </div>
                                )}
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}